import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import CreateQuizPage from './pages/CreateQuizPage';
import EditQuizPage from './pages/EditQuizPage';
import QuizResponsesPage from './pages/QuizResponsesPage';
import AnswerKeyPage from './pages/AnswerKeyPage';
import TakeQuizPage from './pages/TakeQuizPage';
import LoginPage from './pages/LoginPage';
import ReportsDashboardPage from './pages/ReportsDashboardPage';
import AuthSuccessPage from './pages/AuthSuccessPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import NotFoundPage from './pages/NotFoundPage';
import ActiveSessionsPage from './pages/ActiveSessionsPage';
import SendMessagePage from './pages/SendMessagePage';
import NotificationsPage from './pages/NotificationsPage';
// import PDFTestPage from './pages/PDFTestPage';
import ProtectedRoute from './components/ProtectedRoute';
import PageTransition from './components/PageTransition';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/ToastContainer';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import VersionUpdateModal from './components/VersionUpdateModal';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { useVersionCheck } from './hooks/useVersionCheck';
import './App.css';
import './styles/transitions.css';

// App content component that uses the session timeout hook
const AppContent: React.FC = () => {
  const { showWarning, timeRemaining, dismissWarning } = useSessionTimeout({
    timeoutMinutes: 30, // 30 minutes timeout
    warningMinutes: 5   // Show warning 5 minutes before timeout
  });

  const { showUpdate, handleRefresh, handleDismiss } = useVersionCheck();

  return (
    <>
      <VersionUpdateModal
        isOpen={showUpdate}
        onRefresh={handleRefresh}
        onDismiss={handleDismiss}
      />
      {showWarning && timeRemaining !== null && (
        <SessionTimeoutWarning
          timeRemaining={timeRemaining}
          onDismiss={dismissWarning}
          onExtend={dismissWarning}
        />
      )}
      <div className="app">
        <Header />
        <main className="main-content">
          <PageTransition>
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/quiz/:id" element={<TakeQuizPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/success" element={<AuthSuccessPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                
                {/* Development routes
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/pdf-test" element={<PDFTestPage />} />
                )} */}
                
                {/* Protected Admin routes */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireSupervisor>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/quiz/create" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <CreateQuizPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/quiz/:id/edit" 
                  element={
                    <ProtectedRoute requireAdminOrLimitedAdmin>
                      <EditQuizPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/quiz/:id/responses" 
                  element={
                    <ProtectedRoute requireSupervisor>
                      <QuizResponsesPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/quiz/:id/answer-key" 
                  element={
                    <ProtectedRoute requireSupervisor>
                      <AnswerKeyPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/reports" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <ReportsDashboardPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/admin/active-sessions" 
                  element={
                    <ProtectedRoute allowedEmails={['ali@tecorien.com']}>
                      <ActiveSessionsPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/send-message" 
                  element={
                    <ProtectedRoute allowedEmails={['ali@tecorien.com']}>
                      <SendMessagePage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Notifications page (accessible to all authenticated users and guests) */}
                <Route path="/notifications" element={<NotificationsPage />} />
                
                {/* 404 route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
          </PageTransition>
        </main>
      </div>
      </>
  );
};

const App: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  );
};

export default App; 